<script setup lang="ts">
  import { ref } from 'vue';
  import useScrollHelper from '@/composables/useScroll';
  import ThemeSwitcher from '@/components/Shared/ThemeSwitcher.vue';
  import { useAuthStore } from '@/stores/authStore';
  import usePartnerRef from '@/composables/usePartnerRef';

  const authStore = useAuthStore();
  const { registerHref } = usePartnerRef();

  const scrollHelper = useScrollHelper();
  const isMenuOpen = ref(false);

  const navigation = [
    { name: 'Features', href: '#features' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'Team', href: '#team' },
    { name: 'Contact Us', href: '#contact' }
  ];

  const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value;
  };
</script>

<template>
  <header class="fixed inset-x-0 top-0 z-50">
    <!-- Background blur effect -->
    <div class="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md"></div>

    <!-- Border bottom -->
    <div class="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-slate-700 to-transparent"></div>

    <nav
      class="relative mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
      aria-label="Global"
    >
      <!-- Logo -->
      <div class="flex lg:flex-1">
        <a
          href="/"
          class="-m-1.5 p-1.5"
        >
          <Logo />
        </a>
      </div>

      <!-- Mobile menu button -->
      <div class="flex lg:hidden">
        <button
          type="button"
          class="-m-2.5 inline-flex items-center justify-center rounded-lg p-2.5 text-slate-700 dark:text-slate-200"
          @click="toggleMenu"
        >
          <span class="sr-only">Open main menu</span>
          <svg
            v-if="!isMenuOpen"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
          <svg
            v-else
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Desktop navigation -->
      <div class="hidden lg:flex lg:gap-x-8">
        <a
          v-for="item in navigation"
          :key="item.name"
          :href="item.href"
          @click.prevent="scrollHelper.smoothScroll(item.href.slice(1))"
          class="text-sm font-semibold leading-6 text-slate-900 dark:text-slate-100 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200"
        >
          {{ item.name }}
        </a>
      </div>

      <!-- Desktop CTA -->
      <div class="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-6 items-center">
        <a
          v-if="$route.path !== '/login'"
          :href="authStore.isAuthenticated ? '/app' : '/login'"
          class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-slate-900 dark:text-slate-100 transition-colors duration-200"
        >
          {{ authStore.isAuthenticated ? 'Access Dashboard' : 'Log in' }}
        </a>
        <a
          v-if="!authStore.isAuthenticated"
          :href="registerHref"
          class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-slate-900 dark:text-slate-100 transition-colors duration-200"
        >
          Create Account
        </a>
        <ThemeSwitcher class="text-slate-700 dark:text-slate-200 w-10 h-10" />
      </div>
    </nav>

    <!-- Mobile menu -->
    <div
      v-show="isMenuOpen"
      class="lg:hidden"
      role="dialog"
      aria-modal="true"
    >
      <!-- Background backdrop -->
      <div class="fixed inset-0 bg-slate-900/20 dark:bg-slate-900/40 backdrop-blur-sm"></div>

      <div class="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white dark:bg-slate-800 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-slate-900/10 dark:sm:ring-slate-100/10">
        <div class="flex items-center justify-between">
          <a
            href="#"
            class="-m-1.5 p-1.5"
          >
            <Logo />
          </a>
          <button
            type="button"
            class="-m-2.5 rounded-lg p-2.5 text-slate-700 dark:text-slate-200"
            @click="toggleMenu"
          >
            <span class="sr-only">Close menu</span>
            <svg
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div class="mt-6 flow-root">
          <div class="-my-6 divide-y divide-slate-200 dark:divide-slate-700">
            <div class="space-y-2 py-6">
              <a
                v-for="item in navigation"
                :key="item.name"
                :href="item.href"
                @click.prevent="
                  scrollHelper.smoothScroll(item.href.slice(1));
                  toggleMenu();
                "
                class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors duration-200"
              >
                {{ item.name }}
              </a>
            </div>
            <div class="py-6">
              <a
                v-if="$route.path !== '/login'"
                :href="authStore.isAuthenticated ? '/app' : '/login'"
                class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-slate-900 dark:text-slate-100 transition-colors duration-200"
              >
                {{ authStore.isAuthenticated ? 'Access Dashboard' : 'Log in' }}
              </a>
              <a
                v-if="!authStore.isAuthenticated"
                :href="registerHref"
                class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-slate-900 dark:text-slate-100 transition-colors duration-200"
              >
                Create Account
              </a>
              <ThemeSwitcher
                class="text-slate-700 dark:text-slate-200"
                show-text
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
  /* Add smooth transition for mobile menu */
  .lg\:hidden {
    transition: transform 0.3s ease-in-out;
  }

  /* Add hover effect for navigation items */
  a {
    position: relative;
  }

  a::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -2px;
    left: 0;
    background: linear-gradient(to right, #2563eb, #4f46e5);
    transition: width 0.3s ease;
  }

  a:hover::after {
    width: 100%;
  }

  /* Exclude CTA buttons from the underline effect */
  a[href='#']:last-child::after {
    display: none;
  }
</style>
